import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { Module } from '@library/store/modules/modules.interface';
import { CreateNewRoomSuccess } from '@library/store/rooms/rooms.action';
import { RoomsFacade } from '@library/store/rooms/rooms.facade';
import { Room } from '@library/store/rooms/rooms.interface';
import { RoomsAssets } from '@library/utils/constants/rooms';
import { ControlPageAssistantService } from '@pages/control-page/control-page-assistant/control-page-assistant.service';
import { SUPPORTED_CONTROL_WEATHER_MODULES } from '@pages/control-page/control-page.const';
import { Enums } from 'constants/Enums.constant';
import { BehaviorSubject, ReplaySubject, Subscription, firstValueFrom, tap } from 'rxjs';

@Component({
  selector: 'app-assistant-organizing',
  templateUrl: './assistant-organizing.component.html',
  styleUrls: ['./assistant-organizing.component.scss']
})
export class AssistantOrganizingComponent {

  vm = new ReplaySubject<{room: Room, modules: Module[]}[]>(1);
  vm$ = this.vm.asObservable().pipe(
    tap((rooms) => {
      this.addFormControls(rooms);
    }),
  );

  RoomsAssets = RoomsAssets;
  ModulesAssets = {
    [Enums.ModuleType.NAMAIN]: { icon: 'weather-station', name: 'station-app.__MY_DEVICE_NAMAIN' },
    [Enums.ModuleType.NAMODULE1]: { icon: 'outdoor-module', name: 'station-app.__MY_DEVICE_NAMODULE1' },
    [Enums.ModuleType.NAMODULE2]: { icon: 'anemometer', name: 'station-app.__MY_DEVICE_NAMODULE2' },
    [Enums.ModuleType.NAMODULE3]: { icon: 'rain-gauge', name: 'station-app.__MY_DEVICE_NAMODULE3' },
    [Enums.ModuleType.NAMODULE4]: { icon: 'indoor-module', name: 'station-app.__MY_DEVICE_NAMODULE4' },
  };

  sub = new Subscription();

  loader = new BehaviorSubject(false);
  loader$ = this.loader.asObservable();

  form = new FormGroup<Record<string, FormControl<string>>>({});

  constructor(
    private roomsFacade: RoomsFacade,
    private modulesFacade: ModulesFacade,
    private homesFacade: HomesFacade,
    private assistantService: ControlPageAssistantService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  async ngOnInit() {
    const data = [] as {room: Room, modules: Module[]}[];
    const rooms = await firstValueFrom(this.roomsFacade.currentHomeRooms$);
    for (const room of rooms) {
      const modules = await firstValueFrom(this.modulesFacade.getCurrentHomeModulesByRoomId(room.id));
      data.push({
        room: room,
        modules: modules.filter(m =>
          [...SUPPORTED_CONTROL_WEATHER_MODULES].includes(m.type)
        ),
      });
    }

    this.vm.next(data);

    const sub = this.assistantService.newRooms$.subscribe(async (newRooms) => {
      const vm = await firstValueFrom(this.vm$);
      const rooms = await firstValueFrom(this.roomsFacade.currentHomeRooms$);
      const roomIds = rooms.map(r => r.id);

      // When modules are automatically placed in already existing rooms
      // we need to merge them together
      const existingRooms = newRooms.filter(r => roomIds.includes(r.room.id));
      vm.forEach(r => {
        const room = existingRooms.find(existingRoom => existingRoom.room.id == r.room.id);
        if (room) {
          r.modules = [...r.modules, ...room.modules];
        }
      });

      this.vm.next([...newRooms.filter(r => !roomIds.includes(r.room.id)), ...vm]);
    });
    this.sub.add(sub);
  }

  async drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }
    else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }

    const vm = await firstValueFrom(this.vm$);
    this.vm.next(vm);
  }

  private addFormControls(rooms: {room: Room, modules: Module[]}[]) {
    rooms.forEach(r => {
      if (!this.form.controls[r.room.id]) {
        this.form.addControl(r.room.id, new FormControl(r.room.name));
      }
      r.modules.forEach(m => {
        if (!this.form.controls[m.id]) {
          this.form.addControl(m.id, new FormControl(m.name));
        }
      });
    });
  }

  // Always insert item at the end of the room
  sortPredicate(index: number, item: CdkDrag, drop: CdkDropList) {
    return index === drop.data.length;
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  async placeProducts(vm: {room: Room, modules: Module[]}[]) {
    const loading = await firstValueFrom(this.loader);
    if (loading) {
      return;
    }

    this.loader.next(true);

    const homeRooms = await firstValueFrom(this.roomsFacade.currentHomeRooms$);
    const homeRoomsId = homeRooms.map(r => r.id);

    for (const {room, modules} of vm) {
      if (!homeRoomsId.includes(room.id)) {
        await this.createNewRoom(room);
      }
      else {
        await this.updateRoomName(room);
      }
      for (const module of modules) {
        await this.assignModuleToRoom(room, module);
        await this.updateModuleName(module);
      }
    }

    this.document.location.reload();
  }

  private async createNewRoom(room: Room) {
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);

    const res = await firstValueFrom(this.roomsFacade.createNewRoom({
      home_id: homeId,
      name: room.name,
      type: room.type,
    })) as CreateNewRoomSuccess;
    room.id = res.payload.room_id;
  }

  private async updateRoomName(room: Room) {
    const homeRooms = await firstValueFrom(this.roomsFacade.currentHomeRooms$);
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);

    if (homeRooms.find(r => r.id === room.id).name !== this.form.get(room.id).value) {
      await firstValueFrom(this.roomsFacade.updateRoomName({
        home_id: homeId,
        room_id: room.id,
        name: this.form.get(room.id).value,
      }));
    }
  }

  private async assignModuleToRoom(room: Room, module: Module) {
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);

    if (module.bridge) {
      await firstValueFrom(this.modulesFacade.assignModuleToRoom({
        home_id: homeId,
        device_id: module.bridge,
        module_id: module.id,
        room_id: room.id,
      }));
    }
    else {
      await firstValueFrom(this.modulesFacade.assignModuleToRoom({
        home_id: homeId,
        device_id: module.id,
        room_id: room.id,
      }));
    }

  }

  private async updateModuleName(module: Module) {
    const homeId = await firstValueFrom(this.homesFacade.currentHomeId$);
    const homeModules = await firstValueFrom(this.modulesFacade.currentHomeModules$);

    if (homeModules.find(m => m.id).name !== this.form.get(module.id).value) {
      if (module.bridge) {
        await firstValueFrom(this.modulesFacade.updateModuleName({
          home_id: homeId,
          device_id: module.bridge,
          module_id: module.id,
          name: this.form.get(module.id).value,
        }));
      }
      else {
        await firstValueFrom(this.modulesFacade.updateModuleName({
          home_id: homeId,
          module_id: module.id,
          device_id: module.bridge ?? module.id,
          name: this.form.get(module.id).value,
        }));
      }
    }
  }

  roomHasMultipleDevices(modules: Module[]) {
    // Returns true if it has twice the same device or 2 of the same data type (NAModule1 et NaModule4 -> both temperature)
    const dataCollectorDevices = ['NAMain', 'NAModule1', 'NAModule4', 'NATherm1', 'NRV'];
    return  dataCollectorDevices.find(collectorDevice => {
      return modules.filter(mod => mod.type === collectorDevice ).length > 1;
    }) || modules.filter(mod => mod.type === 'NAModule1' || mod.type === 'NAModule4').length > 1;
  }
}
